import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { initDB } from "@/lib/offlineDB";
import { 
  isOnline as checkOnline, 
  syncOfflineData, 
  initialSync,
  getOfflineQueue,
  getLastSyncTime,
  getSyncStatus,
} from "@/lib/offlineService";
import { toast } from "sonner";

const OfflineContext = createContext(null);

// Sync status types for UI indicator
export const SYNC_STATUS = {
  ONLINE_SYNCED: 'online_synced',
  OFFLINE_PENDING: 'offline_pending',
  SYNCING: 'syncing',
  SYNC_FAILED: 'sync_failed',
};

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingActions, setPendingActions] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [dbReady, setDbReady] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  // Compute the sync status for UI
  const syncStatus = syncing 
    ? SYNC_STATUS.SYNCING 
    : syncFailed 
      ? SYNC_STATUS.SYNC_FAILED
      : !online 
        ? SYNC_STATUS.OFFLINE_PENDING
        : pendingActions > 0 
          ? SYNC_STATUS.OFFLINE_PENDING
          : SYNC_STATUS.ONLINE_SYNCED;

  // Initialize IndexedDB
  useEffect(() => {
    initDB().then(() => {
      setDbReady(true);
      updatePendingCount();
      loadLastSync();
    });
  }, []);

  // Update pending actions count
  const updatePendingCount = useCallback(async () => {
    try {
      const queue = await getOfflineQueue();
      setPendingActions(queue.length);
    } catch (error) {
      console.error('Failed to get pending count:', error);
    }
  }, []);

  // Load last sync time
  const loadLastSync = useCallback(async () => {
    try {
      const time = await getLastSyncTime();
      setLastSync(time);
    } catch (error) {
      console.error('Failed to get last sync time:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      setOnline(true);
      setSyncFailed(false);
      toast.success("Back online!");
      
      // Auto-sync when coming online
      if (pendingActions > 0) {
        toast.info("Syncing offline changes...");
        await performSync();
      } else {
        // Just refresh data from server
        await initialSync();
      }
    };

    const handleOffline = () => {
      setOnline(false);
      toast.warning("You're offline. Changes will sync when connected.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pendingActions]);

  // Listen for service worker sync messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SYNC_REQUIRED') {
        performSync();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  // Perform sync
  const performSync = useCallback(async () => {
    if (!checkOnline() || syncing) return null;
    
    setSyncing(true);
    setSyncFailed(false);
    setConflicts([]);
    
    try {
      const result = await syncOfflineData();
      setLastSyncResult(result);
      
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} offline action(s)`);
      }
      
      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        result.conflicts.forEach(conflict => {
          toast.warning(conflict.message, {
            duration: 5000,
          });
        });
      }
      
      if (result.failed > 0) {
        setSyncFailed(true);
        toast.error(`Failed to sync ${result.failed} action(s). Click retry to try again.`);
      }
      
      await updatePendingCount();
      await loadLastSync();
      
      return result;
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncFailed(true);
      toast.error("Sync failed. Click retry to try again.");
      return null;
    } finally {
      setSyncing(false);
    }
  }, [syncing, updatePendingCount, loadLastSync]);

  // Register for background sync
  const registerBackgroundSync = useCallback(async () => {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-offline-actions');
      } catch (error) {
        // Background sync not supported - silent fail
      }
    }
  }, []);

  // Add action and trigger background sync registration
  const recordOfflineAction = useCallback(async () => {
    await updatePendingCount();
    await registerBackgroundSync();
  }, [updatePendingCount, registerBackgroundSync]);

  // Clear conflicts after user acknowledges
  const clearConflicts = useCallback(() => {
    setConflicts([]);
  }, []);

  // Format last sync time for display
  const formatLastSync = useCallback(() => {
    if (!lastSync) return 'Never';
    
    const now = Date.now();
    const diff = now - lastSync;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
    
    return new Date(lastSync).toLocaleDateString();
  }, [lastSync]);

  const value = {
    online,
    syncing,
    pendingActions,
    lastSync,
    dbReady,
    syncFailed,
    syncStatus,
    conflicts,
    lastSyncResult,
    performSync,
    recordOfflineAction,
    updatePendingCount,
    clearConflicts,
    formatLastSync,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error("useOffline must be used within OfflineProvider");
  }
  return context;
}
